import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../../lib/cn.js';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  /**
   * Adds a press-and-hold control that reveals the value while held.
   *
   * Hold rather than toggle, deliberately: a toggle leaves the password sitting
   * in plain text on screen until someone remembers to turn it off, which on a
   * shared or projected screen is the failure this is meant to prevent. Holding
   * bounds the exposure to exactly as long as you are looking.
   */
  revealable?: boolean;
}

/**
 * Label, input, hint and error as one unit. Generating the id here — rather
 * than asking every caller for one — is what makes the label/input association
 * and `aria-describedby` correct by default instead of by discipline.
 */
export function Field({ label, hint, error, className, revealable, ...props }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const [revealed, setRevealed] = useState(false);

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  const type = revealable && revealed ? 'text' : props.type;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
      </label>

      <div className="relative">
        <input
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={cn(
            'block w-full rounded-lg px-3 py-2 text-sm',
            'bg-white text-slate-900 ring-1 ring-slate-300 placeholder:text-slate-400',
            'dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder:text-slate-500',
            'focus:ring-2 focus:ring-brand-500 focus:outline-none',
            error && 'ring-red-500 dark:ring-red-500',
            // Room for the control, so a long password never runs under it.
            revealable && 'pr-10',
            className,
          )}
          {...props}
          type={type}
        />

        {revealable && (
          <button
            type="button"
            // Pointer events rather than mouse ones, so a touch press-and-hold
            // works the same way. `setPointerCapture` is what keeps the release
            // arriving here even if the finger drifts off the button.
            onPointerDown={(event) => {
              setRevealed(true);
              // Capture keeps the release arriving here even if the finger
              // drifts off the button — but it throws for a pointer the browser
              // does not consider active, and an exception here would mean the
              // reveal never happened at all. Reveal first, capture if we can.
              try {
                event.currentTarget.setPointerCapture(event.pointerId);
              } catch {
                // Without capture, onPointerLeave still ends the reveal.
              }
            }}
            onPointerUp={() => setRevealed(false)}
            onPointerCancel={() => setRevealed(false)}
            // Belt and braces: never leave a password on screen because a
            // pointer event went missing or focus moved away.
            onPointerLeave={() => setRevealed(false)}
            onBlur={() => setRevealed(false)}
            // Holding a key holds the reveal, which is the same gesture without
            // a pointer. Space and Enter both, since either activates a button.
            onKeyDown={(event) => {
              if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault();
                setRevealed(true);
              }
            }}
            onKeyUp={() => setRevealed(false)}
            aria-label={revealed ? 'Hide password' : 'Hold to show password'}
            aria-pressed={revealed}
            title="Hold to show"
            className={cn(
              'absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg',
              'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200',
              'focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:outline-none',
            )}
          >
            {revealed ? (
              <EyeOff className="size-4" aria-hidden="true" />
            ) : (
              <Eye className="size-4" aria-hidden="true" />
            )}
          </button>
        )}
      </div>

      {hint && !error && (
        <p id={hintId} className="text-xs text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
